import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Template, TemplateListResponse, TemplateState, UploadLogoResponse } from '../types';
import {
  atualizarTemplate,
  buscarModeloPortal,
  buscarTemplate,
  criarTemplate,
  excluirTemplate,
  listarTemplates,
  publicarTemplate,
  uploadLogo,
} from './actions';

const initialState: TemplateState = {
  templates: [],
  currentTemplate: null,
  isLoading: false,
  isCreating: false,
  isUploading: false,
  error: null,
  total: 0,
  modeloPortalHtml: null,
  modeloPortalStatus: 'idle',
};

const templateSlice = createSlice({
  name: 'template',
  initialState,
  reducers: {
    clearCurrentTemplate: (state) => {
      state.currentTemplate = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setCurrentTemplate: (state, action: PayloadAction<Template>) => {
      state.currentTemplate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(buscarModeloPortal.pending, (state) => {
        state.modeloPortalStatus = 'loading';
      })
      .addCase(buscarModeloPortal.fulfilled, (state, action: PayloadAction<string>) => {
        state.modeloPortalHtml = action.payload;
        state.modeloPortalStatus = 'succeeded';
      })
      .addCase(buscarModeloPortal.rejected, (state) => {
        state.modeloPortalStatus = 'failed';
      })

      .addCase(listarTemplates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(listarTemplates.fulfilled, (state, action: PayloadAction<TemplateListResponse>) => {
        state.isLoading = false;
        state.templates = action.payload.items;
        state.total = action.payload.total;
      })
      .addCase(listarTemplates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(buscarTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(buscarTemplate.fulfilled, (state, action: PayloadAction<Template>) => {
        state.isLoading = false;
        state.currentTemplate = action.payload;
      })
      .addCase(buscarTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(criarTemplate.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(criarTemplate.fulfilled, (state, action: PayloadAction<Template>) => {
        state.isCreating = false;
        state.currentTemplate = action.payload;
        state.templates.unshift(action.payload);
      })
      .addCase(criarTemplate.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })

      .addCase(atualizarTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(atualizarTemplate.fulfilled, (state, action: PayloadAction<Template>) => {
        state.isLoading = false;
        state.currentTemplate = action.payload;
        const index = state.templates.findIndex((t) => t.id === action.payload.id);
        if (index !== -1) {
          state.templates[index] = action.payload;
        }
      })
      .addCase(atualizarTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(publicarTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(publicarTemplate.fulfilled, (state, action: PayloadAction<Template>) => {
        state.isLoading = false;
        state.currentTemplate = action.payload;
        const index = state.templates.findIndex((t) => t.id === action.payload.id);
        if (index !== -1) {
          state.templates[index] = action.payload;
        }
      })
      .addCase(publicarTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(excluirTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(excluirTemplate.fulfilled, (state, action: PayloadAction<string>) => {
        state.isLoading = false;
        state.templates = state.templates.filter((t) => t.id !== action.payload);
        if (state.currentTemplate?.id === action.payload) {
          state.currentTemplate = null;
        }
      })
      .addCase(excluirTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(uploadLogo.pending, (state) => {
        state.isUploading = true;
        state.error = null;
      })
      .addCase(uploadLogo.fulfilled, (state, _action: PayloadAction<UploadLogoResponse>) => {
        state.isUploading = false;
      })
      .addCase(uploadLogo.rejected, (state, action) => {
        state.isUploading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearCurrentTemplate, setCurrentTemplate } = templateSlice.actions;
export const templateReducer = templateSlice.reducer;
